"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StudioEthos() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const principles = [{
    number: "01",
    label: "Premium Tube Amplification"
  }, {
    number: "02",
    label: "Live Tracking Capabilities"
  }, {
    number: "03",
    label: "Community-Driven Culture"
  }];
  return <section data-section-id="3091" id="studio-ethos" className="bg-background text-foreground relative overflow-hidden" style={{
    ["--background" as never]: "hsl(220 15% 5%)",
    ["--foreground" as never]: "hsl(0 0% 95%)"
  }}>
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[700px]">
        {}
        <div ref={imageRef} data-index={0} className={`relative w-full h-[60vw] sm:h-[50vw] lg:h-auto lg:min-h-[700px] overflow-hidden transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 scale-100" : "opacity-0 scale-105"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/garage_room_live_session.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/garage_room_live_session.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/garage_room_live_session.png-wvc-sizes" alt="A gritty, high-contrast shot of a local band tracking live in the studio with warm mustard lighting" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125" />

          {}
          <div className="hidden lg:block absolute left-6 bottom-8 font-mono text-[10px] tracking-[0.25em] uppercase text-primary/85" style={{
          writingMode: "vertical-rl",
          transform: "rotate(180deg)"
        }}>
            — SECTION_01 // THE_GARAGE_ROOM / LIVE_TRACKING
          </div>

          {}
          <div className="lg:hidden absolute left-4 bottom-4 font-mono text-[10px] tracking-[0.25em] uppercase text-primary/85">
            — SECTION_01 // THE_GARAGE_ROOM
          </div>
        </div>

        {}
        <div ref={contentRef} className={`flex items-center bg-background transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        padding: "clamp(3rem, 6vw, 8rem)"
      }}>
          <div className="w-full max-w-[520px]">
            {}
            <div className="font-mono uppercase mb-8 lg:mb-12" style={{
            fontSize: "clamp(0.75rem, 1vw, 0.875rem)",
            letterSpacing: "0.2em",
            color: "hsl(45 90% 50%)"
          }}>
              Studio Ethos — 01
            </div>

            {}
            <h2 className="font-default font-bold text-foreground mb-8 lg:mb-10 uppercase" style={{
            fontSize: "clamp(2.5rem, 5vw, 4rem)",
            lineHeight: "1.0",
            letterSpacing: "-0.02em"
          }}>
              We don&apos;t just record.
              <br />
              <span className="italic font-bold" style={{
              color: "hsl(45 90% 50%)"
            }}>
                We capture energy.
              </span>
            </h2>

            {}
            <div className="space-y-5 mb-10 lg:mb-12 font-default text-foreground/85" style={{
            fontSize: "clamp(1rem, 1vw, 1.125rem)",
            lineHeight: "1.6",
            maxWidth: "460px"
          }}>
              <p>
                The Garage Room was built on the belief that the best music happens when the 
                vibe is raw and the gear is premium. We provide the backline of your dreams—tube 
                amps that roar and kits that crack—so you can focus on the performance.
              </p>
              <p>
                This isn&apos;t a sterile laboratory; it&apos;s a community hub for the loud, the 
                bold, and the DIY-spirited. Whether you&apos;re tracking a live EP or just 
                rehearsing for the next gig, we provide the signal chain that honors your sound.
              </p>
            </div>

            {}
            <ul className="space-y-4 mb-10 lg:mb-12">
              {principles.map((item, i) => <li data-index={i} key={item.number} className="flex items-baseline gap-5 border-t border-border pt-4">
                  <span className="font-mono text-primary" style={{
                fontSize: "12px",
                letterSpacing: "0.15em"
              }}>
                    {item.number}
                  </span>
                  <span className="font-default font-semibold text-foreground uppercase" style={{
                fontSize: "18px",
                letterSpacing: "0.01em"
              }}>
                    {item.label}
                  </span>
                </li>)}
            </ul>

            {}
            <Button asChild variant="outline" size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 has-[>svg]:px-4 group bg-primary border-none hover:bg-primary/90 hover:translate-x-1 hover:translate-y-1 shadow-md rounded-md px-7 h-12 font-mono uppercase tracking-[0.2em] text-[11px] transition-all duration-0 text-primary">
              <Link to="/TheGearPage">
                The Arsenal
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}